import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDateTime } from '../../utils/formatters.js';

const STATUS_VARIANT = { IN_STOCK: 'success', ISSUED: 'neutral' };

export function SerialDetailModal({ serialId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['inventory', 'serial', serialId], queryFn: () => inventoryApi.getSerial(serialId).then((r) => r.items), enabled: Boolean(serialId) });
  const unit = detail.data;

  const issue = useMutation({
    mutationFn: () => inventoryApi.issueSerial({ productId: unit.productId, serialNumber: unit.serialNumber, reference: reference || null }),
    onSuccess: () => {
      setError(null); setReference('');
      queryClient.invalidateQueries({ queryKey: ['inventory', 'serials'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'serial', serialId] });
    },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  return (
    <Modal
      open={Boolean(serialId)}
      onClose={onClose}
      title={unit ? `${unit.serialNumber}` : t('sn.detail.title')}
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
    >
      {unit && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <Badge variant={STATUS_VARIANT[unit.status]}>{t(`sn.status.${unit.status}`)}</Badge>
            <span className="mono">{unit.productSku}</span> {unit.productName}
          </p>

          {unit.status === 'IN_STOCK' && (
            <div className="field-row" style={{ alignItems: 'flex-end' }}>
              <label className="field"><span className="field-label">{t('sn.field.reference')}</span>
                <input className="input" placeholder="SO-…" value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
              <Button variant="primary" disabled={issue.isPending} onClick={() => issue.mutate()}>{t('sn.action.issue')}</Button>
            </div>
          )}

          <div className="settings-divider">{t('sn.lifecycle')}</div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('sn.mv.type')}</th><th>{t('sn.field.reference')}</th><th>{t('sn.mv.time')}</th></tr></thead>
              <tbody>
                {(unit.movements || []).map((m) => (
                  <tr key={m.id}>
                    <td><Badge variant={m.type === 'RECEIVED' ? 'success' : 'warning'}>{t(`sn.mv.${m.type}`)}</Badge></td>
                    <td>{m.reference || '-'}</td>
                    <td className="muted text-sm">{formatDateTime(m.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
